/**
 * L1 §5 — coverage. The flagship panel, and the strongest argument for the
 * whole board (concept §3b).
 *
 * Neither system can answer the question on its own: the Veeam spreadsheet
 * knows nothing about Azure Backup, Azure Backup knows nothing about Veeam, so
 * today **nobody can say whether a given server is backed up, by what, and when
 * it was last proven**. This panel joins the server inventory against every
 * protection source and answers exactly that — deliberately source-agnostic,
 * because "backed up by something" is the question and "backed up by Veeam" is
 * an implementation detail.
 *
 * Five honesty rules are load-bearing here, and most of them are about *not
 * showing a number*:
 *
 * 1. **A stale inventory withholds the counts.** The backend sets
 *    `inventory_stale` when the sweep is missing or old, and then the
 *    unprotected and orphan figures are not to be trusted. An unprotected count
 *    computed from a half-loaded inventory is worse than none, so the panel
 *    says so instead of drawing a donut.
 * 2. **A finding is not the same as a decision.** The headline counts only
 *    servers that are unprotected *and* unexplained. Backup infrastructure does
 *    not back itself up, an appliance is restored from its configuration and a
 *    pooled desktop carries no state — those are listed beside the finding with
 *    their reason, never inside it and never hidden.
 * 3. **Protection is not proof.** A protection item's existence says nothing;
 *    the date of its newest restore point says everything. Protected servers
 *    whose newest point is outside the freshness window get their own panel,
 *    because that is the failure a green tile hides.
 * 4. **"Protected by both" is surfaced, not celebrated.** It is safe and it is
 *    paid for twice.
 * 5. **Orphaned protection items are called out.** An item whose source
 *    resource no longer resolves inflates the protected count while billing for
 *    a machine that is gone — false assurance, which is precisely what this
 *    board exists to remove.
 *
 * Two numbers deliberately sit *outside* the coverage denominator, each in its
 * own panel, because folding them in is what made this panel wrong by a factor
 * of five on 2026-08-24 — 310 findings reported against a true 14:
 *
 * - **Stale Arc registrations** — machines that stopped reporting, mostly
 *   decommissioned. A cleanup list, not a backup gap.
 * - **Unmanaged protected servers** — machines a backup source protects that
 *   Azure and Arc have never seen. They are backed up, so they are not a backup
 *   finding; they mean the estate is bigger than the inventory claims, which is
 *   an Arc onboarding finding and a Defender one.
 */

import { Chart } from "@norican/dataviz";
import { Icon } from "@norican/icons";
import { Alert, DataGrid, EmptyState, Meter, Statistic, Typography } from "@norican/react";

import { ChartCard } from "@/components/board";
import type { BackupCoverage, BackupCoverageRow } from "@/lib/api";
import { formatCount, formatRelativeTime } from "@/lib/charts/config";
import { DrillLink, useDrillNavigate } from "@/lib/drill";

import { PanelCard } from "./panel-card";
import { BACKUP_COVERAGE_PATH, PROTECTION_PARAM } from "./params";
import { COVERAGE_BUCKET_ORDER, COVERAGE_BUCKET_STYLE, type CoverageBucket } from "./status";

/** How many unprotected servers the L1 grid shows — the rest live at L2. */
const WORST_LIMIT = 5;

interface BucketRow {
  bucket: CoverageBucket;
  label: string;
  servers: number;
}

const COVERAGE_CONFIG = Object.fromEntries(
  COVERAGE_BUCKET_ORDER.map((bucket) => [
    bucket,
    { label: COVERAGE_BUCKET_STYLE[bucket].short, color: COVERAGE_BUCKET_STYLE[bucket].color },
  ]),
);

/**
 * Shown only while a source reports jobs without the objects inside them —
 * which is a real state, and a loud one. Job names on this estate name a site
 * and an SLA tier, never a machine, so with no objects the board cannot tell a
 * protected server from an unprotected one and must not pretend otherwise.
 */
const UNKNOWN_MATCH_NOTE =
  "A protection source reported jobs but not the objects inside them, so which servers it covers is unknown. Job names here name a site and an SLA tier, never a machine, so they cannot stand in for the answer — update the collector.";

export function CoverageSection({ coverage }: { coverage: BackupCoverage }) {
  const navigateDrill = useDrillNavigate();

  if (!coverage.configured) {
    return (
      <section id="backup-coverage" className="scroll-mt-6">
        <PanelCard title="Coverage" caption="Not available">
          <Typography variant="body-sm" tone="muted">
            {coverage.status_detail}
          </Typography>
        </PanelCard>
      </section>
    );
  }

  // A coverage number computed from a half-loaded inventory is worse than no
  // number — it looks authoritative and understates the gap.
  if (coverage.inventory_stale) {
    return (
      <section id="backup-coverage" className="scroll-mt-6">
        <PanelCard title="Coverage" caption="Withheld — the server inventory is stale">
          <div className="flex flex-col gap-3">
            <Typography variant="body-sm" tone="muted">
              {coverage.status_detail}
            </Typography>
            <Typography variant="body-sm" tone="muted">
              Coverage is a join between the server inventory and the protection sources. With the
              inventory stale, &ldquo;backed up by nothing&rdquo; would be counted against a list of
              servers that is itself incomplete, so the count is withheld rather than guessed.
              {coverage.inventory_seen_at
                ? ` Last inventory sweep: ${formatRelativeTime(coverage.inventory_seen_at)}.`
                : " The inventory sweep has never run."}
            </Typography>
          </div>
        </PanelCard>
      </section>
    );
  }

  const rows: BucketRow[] = [
    { bucket: "none", label: COVERAGE_BUCKET_STYLE.none.short, servers: coverage.unprotected_real },
    {
      bucket: "excluded",
      label: COVERAGE_BUCKET_STYLE.excluded.short,
      servers: coverage.unprotected_intentional,
    },
    { bucket: "orphaned", label: COVERAGE_BUCKET_STYLE.orphaned.short, servers: coverage.orphaned },
    { bucket: "both", label: COVERAGE_BUCKET_STYLE.both.short, servers: coverage.protected_both },
    {
      bucket: "veeam",
      label: COVERAGE_BUCKET_STYLE.veeam.short,
      servers: coverage.protected_veeam,
    },
    {
      bucket: "azure-backup",
      label: COVERAGE_BUCKET_STYLE["azure-backup"].short,
      servers: coverage.protected_azure_backup,
    },
  ];
  const donutRows = rows.filter((row) => row.servers > 0);

  const inScope = coverage.servers_total;
  // The ratio is measured against the servers somebody *meant* to protect.
  // Counting the backup servers, the appliances and the desktop pool in the
  // denominator would report a permanent shortfall that no action can close,
  // and a percentage nobody can ever reach is a percentage nobody reads.
  const expected = Math.max(inScope - coverage.unprotected_intentional, 0);
  const protectedCount = Math.max(expected - coverage.unprotected_real, 0);
  const coveragePct = expected > 0 ? (protectedCount / expected) * 100 : null;

  return (
    <section id="backup-coverage" className="flex scroll-mt-6 flex-col gap-4">
      {coverage.veeam_match_is_heuristic && (
        <Alert tone="warning" title="Coverage for one source is unknown">
          {UNKNOWN_MATCH_NOTE}
        </Alert>
      )}

      {coverage.unprotected_real > 0 && (
        <Alert
          tone="danger"
          title={`${formatCount(coverage.unprotected_real)} ${
            coverage.unprotected_real === 1 ? "server is" : "servers are"
          } backed up by nothing, with no reason on file`}
        >
          No protection source claims them and no exclusion explains them. A new unprotected server
          surfaces here the day it appears, instead of at the next annual review of the job
          catalogue.
          {coverage.unprotected_intentional > 0
            ? ` A further ${formatCount(coverage.unprotected_intentional)} are unprotected by design — backup infrastructure, appliances, the desktop pool, lab machines — and are listed below with the reason rather than counted here.`
            : ""}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard<BucketRow>
          title="Every server against every protection source"
          caption={`${formatCount(inScope)} live servers in the joined inventory`}
          since={coverage.collected_at}
          tableTwin={{
            id: "backup-coverage-buckets",
            ariaLabel: "Servers per protection bucket",
            data: rows,
            getRowId: (row) => row.bucket,
            columns: [
              { id: "bucket", header: "Protection", accessor: (row) => row.label },
              {
                id: "servers",
                header: "Servers",
                accessor: (row) => row.servers,
                align: "end",
                cell: (row) => <span className="tabular-nums">{formatCount(row.servers)}</span>,
              },
              {
                id: "meaning",
                header: "What it means",
                accessor: (row) => COVERAGE_BUCKET_STYLE[row.bucket].meaning,
              },
            ],
          }}
        >
          {donutRows.length === 0 ? (
            <EmptyState
              size="sm"
              title="No server inventory joined yet"
              description="Coverage needs the Arc and Azure inventories alongside the protection sources."
            />
          ) : (
            <div className="flex flex-col gap-3">
              <div className="relative">
                <Chart.Container
                  config={COVERAGE_CONFIG}
                  className="h-64"
                  aria-label="Servers by what protects them"
                >
                  <Chart.PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <Chart.Tooltip
                      content={
                        <Chart.TooltipContent formatter={(value) => `${value} servers`} hideLabel />
                      }
                    />
                    <Chart.Pie
                      {...Chart.seriesDefaults}
                      data={donutRows}
                      dataKey="servers"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      innerRadius="60%"
                      outerRadius="85%"
                      paddingAngle={2}
                      cornerRadius={3}
                      stroke="var(--norican-surface)"
                      strokeWidth={2}
                      cursor="pointer"
                      onClick={(_, index) => {
                        const row = donutRows[index];
                        if (row) {
                          navigateDrill(BACKUP_COVERAGE_PATH, {
                            [PROTECTION_PARAM]: row.bucket,
                          });
                        }
                      }}
                    >
                      {donutRows.map((row) => (
                        <Chart.Cell
                          key={row.bucket}
                          fill={COVERAGE_BUCKET_STYLE[row.bucket].color}
                        />
                      ))}
                    </Chart.Pie>
                  </Chart.PieChart>
                </Chart.Container>
                {/* The hole carries the total, per the DS donut recipe. */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
                >
                  <span className="text-fg-muted text-xs font-semibold tracking-wide uppercase">
                    Servers
                  </span>
                  <span className="text-fg text-2xl font-bold">{formatCount(inScope)}</span>
                </div>
              </div>

              {/* Buckets are never colour-alone (§3.2). */}
              <div className="text-fg-muted flex flex-wrap justify-center gap-3 text-xs">
                {donutRows.map((row) => {
                  const style = COVERAGE_BUCKET_STYLE[row.bucket];
                  return (
                    <span key={row.bucket} className="inline-flex items-center gap-1.5">
                      <Icon icon={style.icon} size="xs" />
                      {style.short} ({formatCount(row.servers)})
                    </span>
                  );
                })}
              </div>

              {coverage.veeam_match_is_heuristic && (
                <Typography variant="caption" tone="muted">
                  {UNKNOWN_MATCH_NOTE}
                </Typography>
              )}
            </div>
          )}
        </ChartCard>

        <PanelCard
          title="Coverage of the servers meant to be protected"
          caption={
            coveragePct == null
              ? "No inventory to measure against"
              : `${formatCount(protectedCount)} of ${formatCount(expected)} protected`
          }
        >
          <div className="flex flex-col gap-5">
            {coveragePct == null ? (
              <Typography variant="body-sm" tone="muted">
                Coverage is a ratio, and the denominator has not arrived. Nothing is claimed until
                it does.
              </Typography>
            ) : (
              <Meter.Root
                value={coveragePct}
                min={0}
                max={100}
                size="md"
                tone={coveragePct >= 100 ? "success" : coveragePct >= 90 ? "warning" : "danger"}
                aria-valuetext={`${coveragePct.toFixed(1)} per cent of the servers meant to be protected are protected by at least one source`}
              >
                <div className="mb-1 flex items-baseline justify-between gap-3">
                  <Meter.Label>
                    <Typography variant="caption" tone="muted">
                      Protected by at least one source
                    </Typography>
                  </Meter.Label>
                  <Meter.Value>
                    {() => (
                      <Typography variant="caption" tone="muted" tabular>
                        {coveragePct.toFixed(1)}%
                      </Typography>
                    )}
                  </Meter.Value>
                </div>
                <Meter.Track>
                  <Meter.Indicator />
                </Meter.Track>
              </Meter.Root>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Statistic
                label="Protected twice"
                value={formatCount(coverage.protected_both)}
                caption="Two systems, one server — safe, and paid for twice"
              />
              <Statistic
                label="Orphaned items"
                value={formatCount(coverage.orphaned)}
                caption={`Of ${formatCount(
                  coverage.azure_protection_items,
                )} Azure protection items — the source resource no longer resolves`}
              />
            </div>

            {coverage.orphans.length > 0 && (
              <DataGrid<BackupCoverageRow>
                data={coverage.orphans.slice(0, WORST_LIMIT)}
                getRowId={(orphan) => orphan.resource_id ?? orphan.name}
                density="compact"
                aria-label="Orphaned protection items"
                columns={[
                  { id: "item", header: "Item", accessor: (orphan) => orphan.name },
                  {
                    id: "vault",
                    header: "Protected item",
                    accessor: (orphan) => orphan.azure_item ?? "—",
                  },
                  {
                    id: "group",
                    header: "Resource group",
                    accessor: (orphan) => orphan.resource_group ?? "—",
                  },
                  {
                    id: "last",
                    header: "Last backup",
                    accessor: (orphan) => orphan.last_success ?? "",
                    cell: (orphan) => (
                      <span className="text-fg-muted text-xs">
                        {orphan.last_success ? formatRelativeTime(orphan.last_success) : "Never"}
                      </span>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </PanelCard>
      </div>

      <PanelCard
        title="Servers backed up by nothing"
        caption={
          <DrillLink
            to={BACKUP_COVERAGE_PATH}
            params={{ [PROTECTION_PARAM]: "none" }}
            className="text-accent text-sm font-medium hover:underline"
          >
            View all {formatCount(coverage.unprotected_real)} →
          </DrillLink>
        }
        padding={coverage.unprotected.length === 0 ? "md" : "none"}
      >
        {coverage.unprotected.length === 0 ? (
          <EmptyState
            size="sm"
            title="Every server meant to be protected is protected"
            description="Protection is claimed by at least one source for every live server in the join that has no exclusion on file."
          />
        ) : (
          <DataGrid<BackupCoverageRow>
            data={coverage.unprotected.slice(0, WORST_LIMIT)}
            getRowId={(server) => server.resource_id ?? server.name}
            density="compact"
            aria-label="Servers with no protection from any source and no exclusion"
            columns={[
              { id: "name", header: "Server", accessor: (server) => server.name },
              { id: "kind", header: "Seen in", accessor: (server) => server.kind },
              {
                id: "group",
                header: "Resource group",
                accessor: (server) => server.resource_group ?? "—",
              },
              {
                id: "power",
                header: "Power state",
                accessor: (server) => server.power_state ?? "—",
              },
              {
                id: "seen",
                header: "Last seen",
                accessor: (server) => server.last_seen ?? "",
                cell: (server) => (
                  <span className="text-fg-muted text-xs">
                    {server.last_seen ? formatRelativeTime(server.last_seen) : "—"}
                  </span>
                ),
              },
            ]}
          />
        )}
        {coverage.errors.length > 0 && (
          <Typography variant="body-sm" tone="muted">
            Collection problems: {coverage.errors.join(" · ")}
          </Typography>
        )}
      </PanelCard>

      {/*
        Protected on paper, unproven in fact. This is the panel that catches a
        job which quietly stopped producing points while every tile stayed
        green, and it belongs above the exclusion lists because it is a finding.
      */}
      {coverage.stale_protected_rows.length > 0 && (
        <PanelCard
          title="Protected, but not proven"
          caption={`${formatCount(coverage.stale_protected)} with no restore point inside ${formatCount(
            coverage.stale_protection_days,
          )} days`}
          padding="none"
        >
          <DataGrid<BackupCoverageRow>
            data={coverage.stale_protected_rows.slice(0, WORST_LIMIT)}
            getRowId={(server) => server.resource_id ?? server.name}
            density="compact"
            aria-label="Protected servers whose newest restore point is stale"
            columns={[
              { id: "name", header: "Server", accessor: (server) => server.name },
              {
                id: "by",
                header: "Protected by",
                accessor: (server) => server.veeam_object ?? server.azure_item ?? "—",
              },
              {
                id: "points",
                header: "Restore points",
                accessor: (server) => server.restore_points ?? 0,
                align: "end",
                cell: (server) => (
                  <span className="tabular-nums">
                    {server.restore_points == null ? "—" : formatCount(server.restore_points)}
                  </span>
                ),
              },
              {
                id: "proven",
                header: "Last proven",
                accessor: (server) => server.last_success ?? "",
                cell: (server) => (
                  <span className="text-fg-muted text-xs">
                    {server.last_success ? formatRelativeTime(server.last_success) : "Never"}
                  </span>
                ),
              },
            ]}
          />
        </PanelCard>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/*
          Shown, never hidden. An exclusion nobody can see is indistinguishable
          from a bug, and this list is also how a wrong rule gets caught.
        */}
        {coverage.excluded.length > 0 && (
          <PanelCard
            title="Unprotected by design"
            caption={`${formatCount(coverage.unprotected_intentional)} with a reason on file`}
            padding="none"
          >
            <DataGrid<BackupCoverageRow>
              data={coverage.excluded.slice(0, WORST_LIMIT)}
              getRowId={(server) => server.resource_id ?? server.name}
              density="compact"
              aria-label="Servers that are unprotected on purpose"
              columns={[
                { id: "name", header: "Server", accessor: (server) => server.name },
                {
                  id: "why",
                  header: "Why",
                  accessor: (server) => server.exclusion_reason ?? server.exclusion_kind ?? "—",
                },
              ]}
            />
          </PanelCard>
        )}

        {/*
          Backed up, but invisible to Azure and Arc. Not a backup gap — an
          onboarding one, and it means the estate is bigger than the inventory
          says, which makes every count on this board a lower bound (§8).
        */}
        {coverage.unmanaged.length > 0 && (
          <PanelCard
            title="Backed up, but not in Azure or Arc"
            caption={`${formatCount(coverage.unmanaged_protected)} servers a backup source protects that the inventory has never seen`}
            padding="none"
          >
            <DataGrid<BackupCoverageRow>
              data={coverage.unmanaged.slice(0, WORST_LIMIT)}
              getRowId={(server) => server.name}
              density="compact"
              aria-label="Protected servers missing from the Azure and Arc inventory"
              columns={[
                { id: "name", header: "Server", accessor: (server) => server.name },
                {
                  id: "by",
                  header: "Protected by",
                  accessor: (server) => server.veeam_job ?? "—",
                },
                {
                  id: "proven",
                  header: "Last proven",
                  accessor: (server) => server.last_success ?? "",
                  cell: (server) => (
                    <span className="text-fg-muted text-xs">
                      {server.last_success ? formatRelativeTime(server.last_success) : "Never"}
                    </span>
                  ),
                },
              ]}
            />
          </PanelCard>
        )}
      </div>

      {/*
        Deliberately last, and deliberately not danger-toned: these are records,
        not machines. Counting them as unprotected servers is what turned a
        225-server estate into 344 and a 14-server finding into 310.
      */}
      {coverage.stale_registrations > 0 && (
        <PanelCard
          title="Stale Arc registrations"
          caption={`${formatCount(coverage.stale_registrations)} machines stopped reporting — a cleanup list, not a backup gap`}
          padding="none"
        >
          <DataGrid<BackupCoverageRow>
            data={coverage.stale_registrations_rows.slice(0, WORST_LIMIT)}
            getRowId={(server) => server.resource_id ?? server.name}
            density="compact"
            aria-label="Arc registrations whose machine stopped reporting"
            columns={[
              { id: "name", header: "Registration", accessor: (server) => server.name },
              {
                id: "state",
                header: "Agent",
                accessor: (server) => server.agent_status ?? "Unknown",
              },
              {
                id: "group",
                header: "Resource group",
                accessor: (server) => server.resource_group ?? "—",
              },
              {
                id: "seen",
                header: "Last sweep",
                accessor: (server) => server.last_seen ?? "",
                cell: (server) => (
                  <span className="text-fg-muted text-xs">
                    {server.last_seen ? formatRelativeTime(server.last_seen) : "—"}
                  </span>
                ),
              },
            ]}
          />
        </PanelCard>
      )}
    </section>
  );
}
